import { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box } from '@zillow/constellation-10';
import { useRentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks';

import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';
import PropertiesActions from '../../../../actions/PropertiesActions';

import { multiFieldsUtil } from '../../../../utils/postingUtils';
import { postingConstraints } from '../../../../utils/formValidation';
import propertyUtils from '../../../../utils/propertyUtils';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import RMWInput from '../../../../components/RMWInput';
import WizardAlert from '../../../../components/WizardAlert';

import type { ChangeEvent } from 'react';
import type { MockEventProps } from '../types';

const AddressFormField = () => {
  const dispatch = useDispatch();
  const { mutateAsync: saveMultipleFieldsPropertyInfo } = useRentalManagerUserPropertyMultiEditWebApiHandler_multi();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj, isSaveComplete, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty,
      currentProperty: {
        isFeed,
        isArchived,
        listingAlias,
        restrictions: { isUpdateListingRestricted },
        unit,
      },
    },
    user: {
      features: { canUpdateListing },
    },
  } = useSelector((state) => state);

  const { inputs, errorObj, hasFieldError, handleChange: handleFormChange } = useForm({ unit }, postingConstraints);

  const { formattedAddressWithoutUnit } = useMemo(() => propertyUtils.formatAddress(currentProperty), []);

  const isInputFormDisabled = isFeed || !canUpdateListing || isArchived || isUpdateListingRestricted;

  useEffect(() => {
    if (isSaveComplete) {
      // eslint-disable-next-line no-use-before-define
      save({
        target: {
          name: 'unit',
          // @ts-expect-error
          value: inputs.unit,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLSelectElement> | MockEventProps) => {
    const { value, name: fieldName } = event.target;

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const dataToSave = {
      property: {
        [fieldName]: value,
        listingAlias,
      },
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt: DISPLAY_SERVER_ERROR_AT.FIELD,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  return (
    <div data-testid="AddressFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <Box css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
        <RMWInput
          disabled={isInputFormDisabled}
          readOnly={true}
          description="Street address cannot be edited. You can create a new property with the correct address."
          initialValue={formattedAddressWithoutUnit}
          label="Street address"
        />

        <RMWInput
          disabled={isInputFormDisabled}
          description="If the property doesn't have a unit number, leave this field blank."
          // @ts-expect-error
          errorMessage={errorObj.unit}
          fieldName="unit"
          initialValue={unit}
          label="Unit number"
          maxLength="20"
          inputType="adorned"
          leftAdornment="#"
          onBlur={handleChange}
        />
      </Box>
    </div>
  );
};

export default AddressFormField;
